import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'ariv_cookie_consent';
const CONSENT_VERSION = 1; // bump to re-prompt everyone if the policy materially changes

const defaultPreferences = () => ({
  version: CONSENT_VERSION,
  strictlyNecessary: true,   // always on
  functional: false,
  analytics: false,
  decidedAt: null,
});

export const getStoredConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const emitChange = (prefs) => {
  window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: prefs }));
};

const CookieConsentBanner = () => {
  const [prefs, setPrefs] = useState(defaultPreferences());
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // On mount, decide whether to show the banner
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setPrefs(stored);
      setVisible(false);
    } else {
      setVisible(true);
    }

    const openHandler = () => {
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener('open-cookie-preferences', openHandler);
    return () => window.removeEventListener('open-cookie-preferences', openHandler);
  }, []);

  const persist = (next) => {
    const payload = { ...next, version: CONSENT_VERSION, decidedAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setPrefs(payload);
    emitChange(payload);
    setVisible(false);
    setExpanded(false);
  };

  const acceptAll = () => persist({ ...prefs, strictlyNecessary: true, functional: true, analytics: true });
  const rejectAll = () => persist({ ...prefs, strictlyNecessary: true, functional: false, analytics: false });
  const saveCustom = () => persist(prefs);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[1000] p-4 md:p-5"
    >
      <div className="max-w-5xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden">
        {!expanded && (
          <div className="p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 text-sm text-slate-700 leading-relaxed">
              <p className="font-semibold text-[#1F3A68] mb-1">We use cookies</p>
              <p>
                We use strictly-necessary cookies to run Ariv, and — with your permission — analytics cookies to help us improve the Platform. See our{' '}
                <Link to="/cookies" className="text-[#1F3A68] underline">Cookie Policy</Link> and{' '}
                <Link to="/privacy" className="text-[#1F3A68] underline">Privacy Policy</Link>.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => setExpanded(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 border border-slate-300"
              >
                Manage preferences
              </button>
              <button
                onClick={rejectAll}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 border border-slate-300"
              >
                Reject non-essential
              </button>
              <button
                onClick={acceptAll}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#1F3A68] hover:bg-[#1F3A68]/90"
              >
                Accept all
              </button>
            </div>
          </div>
        )}

        {expanded && (
          <div className="p-5 md:p-6">
            <h2 className="text-lg font-bold text-[#1F3A68] mb-2">Cookie preferences</h2>
            <p className="text-sm text-slate-600 mb-4">
              Choose which categories of cookies you accept. Strictly-necessary cookies cannot be disabled because the Platform relies on them.
            </p>

            <div className="space-y-3 mb-5">
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                <input type="checkbox" checked disabled className="mt-1" />
                <div className="flex-1">
                  <div className="font-semibold text-slate-800 text-sm">Strictly necessary</div>
                  <div className="text-xs text-slate-600">Sign-in, session, fraud prevention on payment pages. Always on.</div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={prefs.functional}
                  onChange={e => setPrefs(p => ({ ...p, functional: e.target.checked }))}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold text-slate-800 text-sm">Functional</div>
                  <div className="text-xs text-slate-600">Remember UI preferences such as collapsed sidebars and language.</div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={e => setPrefs(p => ({ ...p, analytics: e.target.checked }))}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold text-slate-800 text-sm">Analytics</div>
                  <div className="text-xs text-slate-600">Anonymised usage analytics (PostHog) so we can improve the Platform.</div>
                </div>
              </label>
            </div>

            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={rejectAll}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 border border-slate-300"
              >
                Reject non-essential
              </button>
              <button
                onClick={saveCustom}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#1F3A68] hover:bg-[#1F3A68]/90"
              >
                Save preferences
              </button>
              <button
                onClick={acceptAll}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 border border-slate-300"
              >
                Accept all
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
